/**
 * Workflow configuration resolution (Stage 4).
 * Resolves effective config from a workflow or returns canonical defaults when none.
 * Single canonical resolver: resolveEffectiveWorkflowConfig(workspaceId, cycleId?, trackId?, sessionId?).
 * Doc 04 §10.2 canonical defaults; WORKFLOW_DOMAIN_MODEL_AND_SCHEMA_DESIGN.md §4.
 */

import { getSafeSupabaseClient } from './supabaseSync';
import { fromDbFormat } from './supabaseSync';
import type {
  Workflow,
  WorkflowConfiguration,
  EffectiveWorkflowConfig,
  AllowedSessionMode,
  ResultResolutionStrategy,
  SessionContextMode,
  WorkflowCriteriaGovernance,
  WorkflowLockedAttribute,
} from '../types/prioritization/workflow';
import { SESSION_CONTEXT_MODES } from '../types/prioritization/workflow';

const CANONICAL_CRITERIA_GOVERNANCE: WorkflowCriteriaGovernance = {
  mode: 'cycle_defined',
  lockedAttributes: [],
};

const CANONICAL: EffectiveWorkflowConfig = {
  allowedSessionModes: [...SESSION_CONTEXT_MODES],
  attributeWriteBack: 'direct',
  resultWriteBack: 'to_track',
  governanceWriteBack: 'proposal_eligible',
  resultResolution: 'latest_published',
  allowTrackResolutionOverride: false,
  catchballEnabled: true,
  criteriaGovernance: CANONICAL_CRITERIA_GOVERNANCE,
};

function asAllowedModes(v: unknown): AllowedSessionMode[] {
  if (!Array.isArray(v)) return CANONICAL.allowedSessionModes;
  const allowed = new Set<AllowedSessionMode>(SESSION_CONTEXT_MODES);
  const out = v.filter((m): m is AllowedSessionMode => allowed.has(m as AllowedSessionMode));
  return out.length > 0 ? out : CANONICAL.allowedSessionModes;
}

const RESOLUTION_STRATEGIES: ResultResolutionStrategy[] = ['latest_published', 'aggregate', 'selected_official'];

function asResultResolution(v: unknown): ResultResolutionStrategy {
  if (typeof v === 'string' && RESOLUTION_STRATEGIES.includes(v as ResultResolutionStrategy)) {
    return v as ResultResolutionStrategy;
  }
  return CANONICAL.resultResolution;
}

/**
 * Return effective workflow config for a workflow, or canonical defaults when workflow is null.
 * Used for session mode gating, write-back gating, result resolution default, and catchball gating.
 */
export function getEffectiveWorkflowConfig(workflow: Workflow | null): EffectiveWorkflowConfig {
  if (!workflow?.configuration || typeof workflow.configuration !== 'object') {
    return { ...CANONICAL };
  }
  const c = workflow.configuration as WorkflowConfiguration;
  return {
    allowedSessionModes: asAllowedModes(c.allowed_session_modes),
    attributeWriteBack: c.attribute_write_back === 'none' || c.attribute_write_back === 'session_only' || c.attribute_write_back === 'direct'
      ? c.attribute_write_back
      : CANONICAL.attributeWriteBack,
    resultWriteBack: c.result_write_back === 'none' || c.result_write_back === 'session_only' || c.result_write_back === 'to_track'
      ? c.result_write_back
      : CANONICAL.resultWriteBack,
    governanceWriteBack: c.governance_write_back === 'none' || c.governance_write_back === 'proposal_eligible'
      ? c.governance_write_back
      : CANONICAL.governanceWriteBack,
    resultResolution: asResultResolution(c.result_resolution),
    allowTrackResolutionOverride: typeof c.allow_track_resolution_override === 'boolean' ? c.allow_track_resolution_override : CANONICAL.allowTrackResolutionOverride,
    catchballEnabled: typeof c.catchball_enabled === 'boolean' ? c.catchball_enabled : CANONICAL.catchballEnabled,
    criteriaGovernance: parseCriteriaGovernance(c.criteria_governance),
  };
}

function parseCriteriaGovernance(raw: unknown): WorkflowCriteriaGovernance {
  if (!raw || typeof raw !== 'object') return CANONICAL_CRITERIA_GOVERNANCE;
  const g = raw as Record<string, unknown>;
  const mode = g['mode'] === 'workflow_defined' ? 'workflow_defined' : 'cycle_defined';
  const lockedAttributes: WorkflowLockedAttribute[] = [];
  if (Array.isArray(g['lockedAttributes'] ?? g['locked_attributes'])) {
    const arr = (g['lockedAttributes'] ?? g['locked_attributes']) as unknown[];
    for (const item of arr) {
      if (!item || typeof item !== 'object') continue;
      const a = item as Record<string, unknown>;
      if (typeof a['attributeKey'] !== 'string') continue;
      lockedAttributes.push({
        attributeKey: a['attributeKey'] as string,
        required: a['required'] === true,
        lockedRange: (a['lockedRange'] ?? a['locked_range'] ?? null) as WorkflowLockedAttribute['lockedRange'],
      });
    }
  }
  return { mode, lockedAttributes };
}

/** Input for resolveEffectiveWorkflowConfig. */
export interface ResolveEffectiveWorkflowConfigInput {
  workspaceId: string;
  cycleId?: string | null;
  trackId?: string | null;
  sessionId?: string | null;
}

/**
 * Single canonical resolver: effective workflow config for a given context.
 * Order: canonical defaults → workspace default workflow → cycle workflow →
 * track override (result_resolution only when allowed) → session context mode (constraint).
 * Used by session creation, tool availability, write-back, result resolution, catchball.
 */
export async function resolveEffectiveWorkflowConfig(
  input: ResolveEffectiveWorkflowConfigInput
): Promise<EffectiveWorkflowConfig> {
  const { workspaceId, cycleId, trackId, sessionId } = input;
  const supabase = getSafeSupabaseClient();
  if (!supabase) {
    return { ...CANONICAL };
  }

  let workflowId: string | null = null;
  let workflowRow: Workflow | null = null;

  // 1) Resolve workflow: cycle explicit → workspace default → canonical
  if (cycleId) {
    const { data: cycleRow } = await supabase
      .from('degap_cycles')
      .select('workflow_id')
      .eq('id', cycleId)
      .single();
    const cycle = cycleRow ? fromDbFormat<{ workflowId: string | null }>(cycleRow as Record<string, unknown>) : null;
    if (cycle?.workflowId) {
      workflowId = cycle.workflowId;
    }
  }
  if (!workflowId) {
    const { data: wsRow } = await supabase
      .from('workspaces')
      .select('default_workflow_id')
      .eq('id', workspaceId)
      .single();
    const ws = wsRow ? fromDbFormat<{ defaultWorkflowId: string | null }>(wsRow as Record<string, unknown>) : null;
    if (ws?.defaultWorkflowId) {
      workflowId = ws.defaultWorkflowId;
    }
  }
  if (workflowId) {
    const { data: wfRow } = await supabase
      .from('workflows')
      .select('*')
      .eq('id', workflowId)
      .single();
    if (wfRow) {
      workflowRow = fromDbFormat<Workflow>(wfRow as Record<string, unknown>);
    }
  }

  const base = getEffectiveWorkflowConfig(workflowRow);
  let effectiveResultResolution = base.resultResolution;
  let sessionContextMode: SessionContextMode | null = null;

  // 2) Track override (result_resolution only when workflow allows)
  if (trackId && base.allowTrackResolutionOverride) {
    const { data: trackRow } = await supabase
      .from('tracks')
      .select('result_resolution_override')
      .eq('id', trackId)
      .single();
    const track = trackRow ? fromDbFormat<{ resultResolutionOverride: string | null }>(trackRow as Record<string, unknown>) : null;
    if (track?.resultResolutionOverride && RESOLUTION_STRATEGIES.includes(track.resultResolutionOverride as ResultResolutionStrategy)) {
      effectiveResultResolution = track.resultResolutionOverride as ResultResolutionStrategy;
    }
  }

  // 3) Session context mode (constraint; load when sessionId provided)
  if (sessionId) {
    const { data: sessionRow } = await supabase
      .from('prioritization_sessions')
      .select('session_context_mode')
      .eq('id', sessionId)
      .single();
    const session = sessionRow ? fromDbFormat<{ sessionContextMode: string | null }>(sessionRow as Record<string, unknown>) : null;
    if (session?.sessionContextMode && (SESSION_CONTEXT_MODES as readonly string[]).includes(session.sessionContextMode)) {
      sessionContextMode = session.sessionContextMode as SessionContextMode;
    }
  }

  return {
    ...base,
    resultResolution: effectiveResultResolution,
    criteriaGovernance: base.criteriaGovernance,
    sessionContextMode: sessionContextMode ?? undefined,
    effectiveWorkflowId: workflowId ?? null,
  };
}

export { CANONICAL as CANONICAL_WORKFLOW_CONFIG };
